import { Button, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import WhiteCard from "src/shared/components/WhiteCard";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import StarIcon from "@mui/icons-material/StarOutline";
import { useIsMobile } from "src/hooks/useIsMobile";
import { PaymentInformation } from "src/api/types";
import {
  useRemovePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
} from "src/api/services/paymentMethodsApi";
import { useTranslation } from "react-i18next";

interface Props {
  method: PaymentInformation;
  canDelete?: boolean;
}

const PaymentMethod = ({ method, canDelete = false }: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const component = "payment-methods.payment-method";
  const [setupDefault, { isLoading }] = useSetDefaultPaymentMethodMutation();
  const [removePaymentMethod] = useRemovePaymentMethodMutation();
  const handleDefaultPayment = useCallback(() => {
    if (method.external_id != null)
      setupDefault({
        paymentMethodId: method.external_id,
      });
  }, [method.external_id, setupDefault]);

  const handleDelete = useCallback(() => {
    if (method.external_id != null) removePaymentMethod(method.external_id);
  }, [method.external_id, removePaymentMethod]);

  return (
    <WhiteCard
      role="listitem"
      direction={isMobile ? "column" : "row"}
      justifyContent="space-between"
    >
      <Stack>
        <Typography textTransform="uppercase">
          {method.card_brand}{" "}
          {method.is_default ? (
            <Typography component="span" textTransform="none">
              ({t(`${component}.default`)})
            </Typography>
          ) : null}
        </Typography>
        <Typography>**** **** **** {method.last_4_digits}</Typography>
        <Typography>{method.expiration_date}</Typography>
      </Stack>
      <Stack alignSelf="end">
        <Stack direction="row" gap="8px" alignSelf="end">
          {method.is_default ? null : (
            <Button
              startIcon={<StarIcon />}
              onClick={handleDefaultPayment}
              loading={isLoading}
              variant="text"
            >
              {t(`${component}.save-default`, "Save as default")}
            </Button>
          )}
          {method.is_default || !canDelete ? null : (
            <Button
              variant="text"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              {t(`${component}.delete`, "Delete")}
            </Button>
          )}
        </Stack>
      </Stack>
    </WhiteCard>
  );
};

export default PaymentMethod;
